import { CallbackInfo } from '@/models/callback-info';
import { JourneyPartType } from '@/models/journey-part-type';
import { Option } from '@/models/option';
import { PathSegment } from '@/models/path-segment';
import { QuoteRequestInfo } from '@/models/quote-request-info';
import { StoreState } from '@/store/index';
import { Mutations } from '@/store/mutations';
import { PathUtil } from '@/util/path-util';
import axios from 'axios';
import { Commit, Dispatch } from 'vuex';

export default {
   updatePrice({commit, state}: {commit: Commit; state: StoreState}): void {
      const oldPrice = state.calculatedPrice;
      const newPrice = PathUtil.calculatePriceForPath(state.segmentLog, state.options);
      if (oldPrice !== newPrice) {
         commit(Mutations.SetPrice, newPrice);
      }
   },
   updateExternalPricePart({commit, state}: { commit: Commit; state: StoreState }): void
   {
       const oldExternalPricePart = state.calculatedExternalPricePart;
       const newExternalPricePart = PathUtil.calculateExternalPriceForPath(state.segmentLog, state.options);
       if (oldExternalPricePart !== newExternalPricePart) {
           commit(Mutations.SetExternalPricePart, newExternalPricePart);
       }
   },
   selectPathSegmentAction({commit}: { commit: Commit; state: StoreState }, segment: PathSegment): void {
      if (!segment) {
         // if there is no segment, how did we even get here?!
         return;
      }

      commit(Mutations.SetSelectedSegment, segment);
   },
   pathSegmentSelectionSubmittedAction(
      {
         commit,
         state,
         dispatch
      }: {commit: Commit; state: StoreState; dispatch: Dispatch},
      segment: PathSegment): void {
      // check if segment is null. if it is the case display the first page,
      // no need to update the price
      if (!segment) {
         commit(Mutations.SetChosenSegment, null);
         commit(Mutations.SetPathSummaryActive, false);
         return;
      }

      // set summary available when the user clicked the first entry
      commit(Mutations.EnablePathSummary, true);

      // if there are no child segments, display the summary
      if (segment.children.length < 1 && state.summaryAvailable) {

         commit(Mutations.SetPathSummaryActive, true);
         // update the price to be sure its correct
         dispatch('updatePrice');
         dispatch('updateExternalPricePart');
         return;
      }

      // otherwise set the summary page inactive
      commit(Mutations.SetPathSummaryActive, false);

      // and set the selected segment
      commit(Mutations.SetChosenSegment, segment);
      commit(Mutations.AddSegmentToLog, segment);
      dispatch('updatePrice');
      dispatch('updateExternalPricePart');
   },
   showPathSummaryAction({commit}: {commit: Commit; state: StoreState}): void {
      commit(Mutations.EnablePathSummary, true);
      commit(Mutations.SetPathSummaryActive, true);
   },
   // eslint-disable-next-line @typescript-eslint/no-unused-vars
   optionSelectionChangedAction({commit, dispatch}: { commit: Commit; state: StoreState; dispatch: Dispatch },
      payload: {selected: true; option: Option}): void {
      commit(Mutations.SetOptionSelected, {selected: payload.selected, optionId: payload.option.id});
      // update the price so the chosen option is reflected in the sum
      dispatch('updatePrice');
      dispatch('flashAction');
   },
   async callbackRequestAction(
      {commit, state}: {commit: Commit; state: StoreState},
      payload: CallbackInfo): Promise<void> {
      commit(Mutations.CallbackRequestStarted);

      const journeyIds = state.segmentLog
                              .filter((s: PathSegment) => s.dataObject?.type !== JourneyPartType.AdditionalOptions)
                              .map((pathSegment: PathSegment) => pathSegment.dataObject?.id);

      const additionalOptionsIds = state.options
                                        .filter((o: Option) => o.isSelected)
                                        .map((o: Option) => o.id);

      const requestPayload = {
         ...payload,
         chosenJourney: journeyIds,
         chosenAdditionalOptions: additionalOptionsIds,
         rootId: state.rootId
      };

      try {
         await axios.post('/_contao/funeral-configurator/request/callback', requestPayload);

         commit(Mutations.CallbackRequestSucceeded);
      }
      catch (e) {
         commit(Mutations.CallbackRequestFailed, {message: e});
         setTimeout(() => commit(Mutations.CallbackRequestReset), 2000);
      }
   },
   async quoteRequestAction(
      {commit, state}: {commit: Commit; state: StoreState},
      payload: QuoteRequestInfo): Promise<void> {
      commit(Mutations.QuoteRequestStarted);

      const journeyIds = state.segmentLog
                              .filter((s: PathSegment) => s.dataObject?.type !== JourneyPartType.AdditionalOptions)
                              .map((pathSegment: PathSegment) => pathSegment.dataObject?.id);

      const additionalOptionsIds = state.options
                                        .filter((o: Option) => o.isSelected)
                                        .map((o: Option) => o.id);

      const requestPayload = {
         ...payload,
         chosenJourney: journeyIds,
         chosenAdditionalOptions: additionalOptionsIds,
         rootId: state.rootId
      };

      try {
         await axios.post('/_contao/funeral-configurator/request/quote', requestPayload);

         commit(Mutations.QuoteRequestSucceeded);
      }
      catch (e) {
         commit(Mutations.QuoteRequestFailed, {message: e});
         setTimeout(() => commit(Mutations.QuoteRequestReset), 2000);
      }
   },
   flashAction({commit}: {commit: Commit; state: StoreState}): void {
      commit(Mutations.Flash);
   },
   resetAction({commit}: {commit: Commit; state: StoreState}): void {
      commit(Mutations.Reset);
   }
};

export enum Actions {
   CallbackRequestAction = 'callbackRequestAction',
   FlashAction = 'flashAction',
   OptionSelectionChanged = 'optionSelectionChangedAction',
   SubmitPathSegmentSelection = 'pathSegmentSelectionSubmittedAction',
   SelectPathSegment = 'selectPathSegmentAction',
   ShowPathSummary = 'showPathSummaryAction',
   QuoteRequestAction = 'quoteRequestAction',
   UpdatePrice = 'updatePrice',
   UpdateExternalPricePart = 'updateExternalPricePart',
   UpdateInternalPricePart = 'updateInternalPricePart',
   ResetAction = 'resetAction'
}
